var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useState, useRef, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { Accordion } from '@folio/stripes/components';
import { isControlFieldTag } from '../../utils/marcControlFields';
import { getMarcColors } from '../../utils/marcColors';
/** MARC control fields (00X) have no indicators or subfield codes. */
var isControlField = isControlFieldTag;
var MONO_FONT = "'SF Mono', 'Cascadia Code', 'Consolas', monospace";
var getColors = getMarcColors;
var tagBadgeStyle = function (colors) { return ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '5px 7px',
    fontFamily: MONO_FONT,
    fontWeight: 600,
    fontSize: '0.8rem',
    color: '#fff',
    backgroundColor: colors.dark,
    borderRadius: '5px',
    whiteSpace: 'nowrap',
    letterSpacing: '0.02em',
    flexShrink: 0,
}); };
var sfCodeStyle = function (colors) { return ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4.5px 5px',
    fontFamily: MONO_FONT,
    fontWeight: 700,
    fontSize: '0.75rem',
    color: '#fff',
    backgroundColor: colors.dark,
    opacity: 0.85,
    whiteSpace: 'nowrap',
    borderRadius: '5px 0 0 5px',
}); };
var sfValueStyle = function (colors) { return ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4.5px 8px',
    fontFamily: MONO_FONT,
    fontSize: '0.8rem',
    color: colors.text,
    backgroundColor: colors.light,
    border: "1px solid ".concat(colors.border),
    borderLeft: 'none',
    borderRadius: '0 5px 5px 0',
    whiteSpace: 'nowrap',
}); };
var sfLinkStyle = {
    color: 'inherit',
    textDecoration: 'underline',
    textDecorationStyle: 'dotted',
    textUnderlineOffset: '2px',
};
/** Starts with an http(s) scheme followed by `//`, in any letter case. */
var HTTP_SCHEME_PREFIX = /^https?:\/\//i;
/**
 * Any whitespace, C0 control, DEL or C1 control character. Checked on the trimmed value: the WHATWG URL
 * parser silently strips embedded tab/newline, so `https://trusted.example\n@evil.example/p`
 * would parse, display one host and navigate to another.
 */
// eslint-disable-next-line no-control-regex -- matching control characters is the purpose
var WHITESPACE_OR_CONTROL = /[\s\u0000-\u001f\u007f-\u009f]/;
/**
 * CZNA-1615: the URL an external link from `value` should point to (the trimmed value), or
 * null when the value is not an absolute `http`/`https` URL. Any other scheme (`javascript:`,
 * `urn:`, `ftp:`…), a bare identifier, an unparseable value, or one with whitespace or
 * control characters inside stays text.
 */
var toExternalHttpUrl = function (value) {
    var trimmed = value === null || value === void 0 ? void 0 : value.trim();
    if (!trimmed)
        return null;
    if (WHITESPACE_OR_CONTROL.test(trimmed) || !HTTP_SCHEME_PREFIX.test(trimmed))
        return null;
    try {
        var url = new URL(trimmed);
        return url.protocol === 'http:' || url.protocol === 'https:' ? trimmed : null;
    }
    catch (_a) {
        return null;
    }
};
/** CZNA-1615: only 024 $1 (the URI of the identity at its source) is linked out. */
var isSourceUriSubfield = function (field, sf) {
    return field.fieldTag === '024' && sf.subfieldCode === '1';
};
/** Fixed width for tag badge area so subfields always start at the same position. */
var TAG_BADGE_WIDTH = '72px';
/**
 * Popover showing decomposed components for a subfield.
 * Appears on hover, positioned below the subfield badge.
 */
var ComponentPopover = function (_a) {
    var components = _a.components, anchorRef = _a.anchorRef;
    var intl = useIntl();
    if (!anchorRef)
        return null;
    return (React.createElement("div", { style: {
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: '4px',
            zIndex: 1000,
            backgroundColor: '#fff',
            border: '1px solid #d4d4d8',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            padding: '8px 0',
            minWidth: '200px',
            maxWidth: '360px',
        } },
        React.createElement("div", { style: {
                padding: '2px 10px 6px',
                fontSize: '0.7rem',
                fontWeight: 600,
                color: '#71717a',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
            } },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.authorities.detail.components" })),
        components.map(function (c, i) { return (React.createElement("div", { key: "".concat(c.componentType, "-").concat(i), style: {
                display: 'flex',
                alignItems: 'baseline',
                gap: '8px',
                padding: '3px 10px',
                fontSize: '0.8rem',
            } },
            React.createElement("span", { style: { color: '#71717a', whiteSpace: 'nowrap', flexShrink: 0 } }, intl.formatMessage({
                id: "ui-czna-authorities.componentType.".concat(c.componentType),
                defaultMessage: c.componentType,
            })),
            React.createElement("span", { style: { fontWeight: 500, color: '#18181b' } }, c.value))); })));
};
/**
 * Wrapper for subfield badges that have components — shows popover on hover.
 */
var SubfieldWithComponents = function (_a) {
    var children = _a.children, components = _a.components;
    var _b = useState(false), hovered = _b[0], setHovered = _b[1];
    var ref = useRef(null);
    var timeoutRef = useRef(undefined);
    var handleEnter = useCallback(function () {
        clearTimeout(timeoutRef.current);
        setHovered(true);
    }, []);
    var handleLeave = useCallback(function () {
        timeoutRef.current = setTimeout(function () { return setHovered(false); }, 150);
    }, []);
    return (React.createElement("span", { ref: ref, onMouseEnter: handleEnter, onMouseLeave: handleLeave, style: { position: 'relative', cursor: 'help' } },
        children,
        React.createElement("span", { style: {
                position: 'absolute',
                top: '-2px',
                right: '-2px',
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: '#f59e0b',
                border: '1px solid #fff',
            } }),
        hovered && React.createElement(ComponentPopover, { components: components, anchorRef: ref.current })));
};
/**
 * Renders MARC data fields as badge rows.
 * Each row: tag badge (dark) + subfield badges (code dark + value light).
 * Color is derived from field group (0xx–9xx).
 * Subfields with sourceAuthorityId are rendered as internal links.
 * 024 $1 with an http(s) URI (identity at its source — VIAF, ORCID, Wikidata…) is rendered
 * as an external link opening in a new window (CZNA-1615) — even when it also has
 * sourceAuthorityId, which links internally for every other subfield.
 * Subfields with components show a popover on hover.
 */
var MarcDataDisplay = function (_a) {
    var dataFields = _a.dataFields, _b = _a.noAccordion, noAccordion = _b === void 0 ? false : _b;
    var formatIndicator = function (ind) {
        return !ind || ind.trim() === '' ? '#' : ind;
    };
    var renderSubfieldBadge = function (field, sf, colors) {
        var key = "".concat(field.fieldTag, "-").concat(field.fieldIndex, "-").concat(sf.subfieldCode, "-").concat(sf.subfieldIndex);
        var hasComponents = sf.components && sf.components.length > 0;
        var externalUrl = isSourceUriSubfield(field, sf) ? toExternalHttpUrl(sf.value) : null;
        var badge = (React.createElement("span", { key: key, style: { display: 'inline-flex', alignItems: 'stretch', borderRadius: '5px', overflow: 'visible', flexShrink: 0 } },
            React.createElement("span", { style: sfCodeStyle(colors) }, sf.subfieldCode),
            React.createElement("span", { style: sfValueStyle(colors) }, externalUrl ? (React.createElement("a", { href: externalUrl, target: "_blank", rel: "noopener noreferrer", style: sfLinkStyle }, sf.value)) : sf.sourceAuthorityId ? (React.createElement(Link, { to: "/czna-authorities/authorities/".concat(sf.sourceAuthorityId), style: sfLinkStyle }, sf.value)) : (sf.value))));
        if (hasComponents) {
            return (React.createElement(SubfieldWithComponents, { key: key, components: sf.components }, badge));
        }
        return badge;
    };
    /** Render control field value without subfield code badge. */
    var renderControlFieldValue = function (field, sf, colors) {
        var key = "".concat(field.fieldTag, "-").concat(field.fieldIndex, "-").concat(sf.subfieldIndex);
        return (React.createElement("span", { key: key, style: {
                display: 'inline-flex',
                alignItems: 'center',
                padding: '4.5px 8px',
                fontFamily: MONO_FONT,
                fontSize: '0.8rem',
                color: colors.text,
                backgroundColor: colors.light,
                border: "1px solid ".concat(colors.border),
                borderRadius: '5px',
                whiteSpace: 'nowrap',
            } }, sf.value));
    };
    var renderFieldRow = function (field) {
        var colors = getColors(field.fieldTag);
        var control = isControlField(field.fieldTag);
        // Control fields: just "TAG", data fields: "TAG II" with formatted indicators
        var tagLabel = control
            ? field.fieldTag
            : "".concat(field.fieldTag, " ").concat(formatIndicator(field.indicator1)).concat(formatIndicator(field.indicator2));
        return (React.createElement("div", { key: "".concat(field.fieldTag, "-").concat(field.fieldIndex), "data-testid": "marc-field-".concat(field.fieldTag), style: { display: 'flex', alignItems: 'flex-start', gap: '4px', margin: '4px 0' } },
            React.createElement("span", { style: __assign(__assign({}, tagBadgeStyle(colors)), { width: TAG_BADGE_WIDTH, flexShrink: 0, justifyContent: 'center' }) }, tagLabel),
            React.createElement("div", { style: { display: 'flex', flexWrap: 'wrap', gap: '4px', flex: 1, minWidth: 0 } }, control
                ? field.subfields.map(function (sf) { return renderControlFieldValue(field, sf, colors); })
                : field.subfields.map(function (sf) { return renderSubfieldBadge(field, sf, colors); }))));
    };
    if (dataFields.length === 0) {
        return null;
    }
    var controlFields = dataFields.filter(function (f) { return isControlField(f.fieldTag); });
    var dataFieldsOnly = dataFields.filter(function (f) { return !isControlField(f.fieldTag); });
    var content = (React.createElement("div", { style: { padding: '0.5rem 0' } },
        controlFields.length > 0 && (React.createElement(React.Fragment, null,
            controlFields.map(function (field) { return renderFieldRow(field); }),
            React.createElement("hr", { style: { border: 'none', borderTop: '1px solid #e4e4e7', margin: '8px 0' } }))),
        dataFieldsOnly.map(function (field) { return renderFieldRow(field); })));
    if (noAccordion)
        return content;
    return (React.createElement(Accordion, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.authorities.detail.marcData" }) }, content));
};
export default MarcDataDisplay;
